import { Modal, Stack, Textarea, Button, Alert, Group } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { Job, useUpdateJob } from '../../api/jobs';

interface EditDescriptionModalProps {
  opened: boolean;
  onClose: () => void;
  job: Job | null;
  onSuccess?: () => void;
}

export default function EditDescriptionModal({ opened, onClose, job, onSuccess }: EditDescriptionModalProps) {
  const [description, setDescription] = useState('');
  const updateJobMutation = useUpdateJob();

  useEffect(() => {
    if (opened && job) {
      setDescription(job.description_markdown);
    }
  }, [opened, job]);

  const handleClose = () => {
    setDescription('');
    onClose();
  };

  const handleSave = async () => {
    if (!job) return;

    try {
      await updateJobMutation.mutateAsync({
        jobId: job.id,
        updates: { description_markdown: description },
      });

      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to update job description:', error);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Job Description"
      size="xl"
    >
      <Stack gap="md">
        <Alert icon={<IconAlertCircle size={16} />} color="blue" title="Markdown Format">
          You can use Markdown formatting to structure your description. This is useful for adding headers, lists,
          code blocks, and more.
        </Alert>

        <Textarea
          label="Job Description (Markdown)"
          placeholder="Enter the job description in Markdown format"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={15}
          maxRows={30}
          style={{ fontFamily: 'monospace' }}
        />

        <Group justify="flex-end" gap="md">
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={updateJobMutation.isPending}
            disabled={updateJobMutation.isPending || description === job?.description_markdown}
          >
            Save Description
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
